import api from '../../../shared/services/api';
import { TestAnswer, TestDetail, TestResult } from '../types/test-detail.types';

export class TestDetailService {
  private static readonly BASE_URL = '/tests';

  /**
   * Get test detail with sections, passages and questions
   */
  static async getTestDetail(testId: string): Promise<TestDetail> {
    try {
      const response = await api.get(`${this.BASE_URL}/${testId}`);

      // Validate response structure
      if (
        !response.data ||
        !response.data.id ||
        !Array.isArray(response.data.sections)
      ) {
        throw new Error('Invalid test data received from server');
      }

      return response.data;
    } catch (error: any) {

      if (error.response?.status === 404) {
        throw new Error('Test not found');
      }
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }

      if (error?.code === 'SUBSCRIPTION_LIMIT_REACHED') {
        const subscriptionError = new Error('Subscription limit reached');
        subscriptionError.name = 'SUBSCRIPTION_LIMIT_REACHED';
        throw subscriptionError;
      }
      throw new Error(error.message || 'Error loading test');
    }
  }

  /**
   * Save user's answer for a question
   */
  static async saveAnswer(testId: string, answer: TestAnswer): Promise<void> {
    try {
      await api.post(`${this.BASE_URL}/${testId}/answers`, answer);
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      throw new Error(error.message || 'Error saving answer');
    }
  }

  /**
   * Complete test and get results
   */
  static async completeTest(testId: string): Promise<TestResult> {
    try {
      const response = await api.post<TestResult>(
        `${this.BASE_URL}/${testId}/complete`
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      throw new Error(error.message || 'Error completing test');
    }
  }

  /**
   * Restart test progress
   */
  static async restartTest(testId: string): Promise<void> {
    try {
      await api.post(`${this.BASE_URL}/${testId}/restart`);
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      throw new Error(error.message || 'Error restarting test');
    }
  }
}
